import React, { useCallback, useRef, useState } from 'react'

import {
  getNodeHeight,
  setNodeHeightStyles,
  setNodeInitialHeightStyles
} from './utils/domNodeUtils'
import { useIntersectionObserver } from './utils/useIntersectionObserver'

type Props = {
  className?: string
  fallback: React.ReactNode
  children: React.ReactNode
  memoizeHeight?: boolean
}

/**
 * The given children will only render
 * when the first pixel of fallback component enters the viewport (visible to our user)
 *
 * @param fallback
 * @param children
 */
const LazyInViewport = ({
  fallback,
  children,
  className,
  memoizeHeight = false
}) => {
  const refContent = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  const handleMemoizeHeight = useCallback(
    (entry: IntersectionObserverEntry, node: HTMLDivElement) => {
      /**
       * When scrolling bottom, we memoize the parent node height
       * and apply the height to the content node
       */
      if (visible && !entry.isIntersecting && node) {
        const height = getNodeHeight(node)
        // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
        setNodeHeightStyles(refContent.current, { height })
      }

      /**
       * When scrolling top, we reinitialize the content node height
       * So the height is dynamic based on content
       */
      if (!visible && entry.isIntersecting && node) {
        // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
        setNodeInitialHeightStyles(refContent.current)
      }
    },
    [refContent, visible]
  )

  const [refParent] = useIntersectionObserver((entry, node) => {
    if (memoizeHeight) {
      handleMemoizeHeight(entry, node)
    }

    // Prevent to update state if the intersecting state did not change
    if (visible !== entry.isIntersecting) {
      setVisible(entry.isIntersecting)
    }
  })

  return (
    // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
    <div className={className} ref={refParent}>
      {/* @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333) */}
      <div ref={refContent}>
        {!visible && fallback}
        {visible && children}
      </div>
    </div>
  )
}

export default React.memo<Props>(LazyInViewport)
